/**
 * FUEL IQ DESIGN SYSTEM 2.0 - COMPONENT VARIANTS
 *
 * Premium, reusable component style variants.
 * Use these for consistent, beautiful components throughout the app.
 */

import { SxProps, Theme } from '@mui/material';
import { designTokens } from './designTokens';

const { colors, shadows, gradients, borderRadius, transitions } = designTokens;

// ===== CARD VARIANTS =====
//
// September 2026: every variant is now a member of the ONE card family the
// rest of the app uses — the cyan-bordered cyberpunk card (see
// design-system/cardStyles.ts). The old "Design System 2.0" recipes here had
// white borders, drop shadows, hover lifts and a white hairline, so the few
// places that used them (the fitness widget's empty state, the Garmin and
// Apple Health connection cards, food-log items) looked imported from another
// app. Background and radius come from global.css (`--bg-card`, 12px) on any
// Paper, so they are not set here. Variants now differ only in border weight
// and whether the card reacts to hover.
const CYAN = 'rgba(0,224,255,0.25)';
const CYAN_HOVER = 'rgba(0,224,255,0.35)';
const FAMILY_BASE = {
  border: `1px solid ${CYAN}`,
  boxShadow: '0 0 18px rgba(0,224,255,0.05)',
  position: 'relative',
  overflow: 'hidden',
  transition: `border-color ${transitions.duration.base} ${transitions.timing.smooth}`,
} as const;

export const cardVariants = {
  // Default card
  elevated: { ...FAMILY_BASE } as SxProps<Theme>,

  // Same card — kept for callers
  glass: { ...FAMILY_BASE } as SxProps<Theme>,

  // Card with the cyan accent line (hero / section card)
  gradient: {
    ...FAMILY_BASE,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 16,
      right: 16,
      height: '2px',
      background: '#00E0FF',
      boxShadow: '0 0 12px #00E0FF, 0 0 4px #00E0FF',
      pointerEvents: 'none',
    },
  } as SxProps<Theme>,

  // Quieter card for items inside another card
  outlined: {
    ...FAMILY_BASE,
    border: '1px solid rgba(0,224,255,0.18)',
  } as SxProps<Theme>,

  // List item
  compact: {
    ...FAMILY_BASE,
    border: '1px solid rgba(0,224,255,0.1)',
    padding: '12px 16px',
    '&:hover': { borderColor: CYAN_HOVER },
  } as SxProps<Theme>,

  // Clickable card
  interactive: {
    ...FAMILY_BASE,
    cursor: 'pointer',
    '&:hover': { borderColor: CYAN_HOVER, boxShadow: '0 0 12px rgba(0,224,255,0.12)' },
  } as SxProps<Theme>,
};

// ===== BUTTON VARIANTS =====
export const buttonVariants = {
  // Gradient primary button
  gradientPrimary: {
    background: '#00E0FF !important',
    backgroundColor: '#00E0FF !important',
    color: '#0a0e14 !important',
    fontWeight: 700,
    borderRadius: '8px',
    boxShadow: '0 0 14px rgba(0,224,255,0.28)',
    border: 'none',
    textTransform: 'none',
    '&:hover': { background: '#5CEBFF !important', backgroundColor: '#5CEBFF !important' },
  } as SxProps<Theme>,

  // Gradient secondary button
  gradientSecondary: {
    background: 'transparent !important',
    color: '#00E0FF !important',
    border: '1px solid rgba(0,224,255,0.5)',
    borderRadius: '8px',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': { background: 'rgba(0,224,255,0.08) !important' },
  } as SxProps<Theme>,

  // Glass button
  glass: {
    background: 'transparent !important',
    color: '#00E0FF !important',
    border: '1px solid rgba(0,224,255,0.5)',
    borderRadius: '8px',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': { background: 'rgba(0,224,255,0.08) !important' },
  } as SxProps<Theme>,

  // Outlined glow button
  outlinedGlow: {
    background: 'transparent',
    border: '2px solid',
    borderColor: colors.primary[500],
    color: colors.primary[400],
    fontWeight: 600,
    borderRadius: borderRadius.md,
    padding: '10px 22px',
    boxShadow: `inset 0 0 20px rgba(0, 255, 107, 0.1)`,
    transition: `all ${transitions.duration.base} ${transitions.timing.smooth}`,
    textTransform: 'none',
    '&:hover': {
      background: 'rgba(0, 255, 107, 0.08)',
      borderColor: colors.primary[400],
      boxShadow: `inset 0 0 30px rgba(0, 255, 107, 0.2), ${shadows.glow.green}`,
      transform: 'translateY(-2px)',
    },
  } as SxProps<Theme>,

  // Icon button
  icon: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: borderRadius.md,
    padding: '10px',
    transition: `all ${transitions.duration.fast} ${transitions.timing.smooth}`,
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderColor: 'rgba(255, 255, 255, 0.15)',
      transform: 'scale(1.05)',
    },
  } as SxProps<Theme>,

  // Floating action button
  floating: {
    background: gradients.primary,
    color: colors.neutral[900],
    boxShadow: `${shadows.dark.lg}, ${shadows.glow.green}`,
    borderRadius: borderRadius.full,
    width: 56,
    height: 56,
    transition: `all ${transitions.duration.base} ${transitions.timing.spring}`,
    '&:hover': {
      background: gradients.primary,
      boxShadow: `${shadows.dark.xl}, ${shadows.glow.green}`,
      transform: 'translateY(-4px) scale(1.05)',
    },
  } as SxProps<Theme>,
};

// ===== CHART & VISUALIZATION STYLES =====
export const chartStyles = {
  // Modern card wrapper for charts
  container: {
    background: gradients.background.card,
    backdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: borderRadius.xl,
    padding: '24px',
    boxShadow: shadows.dark.floating,
  } as SxProps<Theme>,

  // Chart color palette
  colors: {
    primary: [
      colors.primary[500],
      colors.primary[400],
      colors.primary[600],
      colors.primary[300],
      colors.primary[700],
    ],
    multiColor: [
      colors.primary[500],
      colors.secondary.blue[500],
      colors.secondary.magenta[500],
      colors.secondary.indigo[500],
      colors.semantic.warning,
    ],
    gradient: [
      'rgba(0, 255, 107, 0.8)',
      'rgba(0, 224, 255, 0.8)',
      'rgba(255, 0, 230, 0.8)',
      'rgba(79, 70, 229, 0.8)',
      'rgba(250, 255, 0, 0.8)',
    ],
  },

  // Grid and axis styling
  grid: {
    line: {
      stroke: 'rgba(255, 255, 255, 0.05)',
      strokeWidth: 1,
    },
    axis: {
      stroke: 'rgba(255, 255, 255, 0.1)',
      strokeWidth: 1.5,
    },
  },

  // Tooltip styling for charts
  tooltip: {
    background: gradients.background.elevated,
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: borderRadius.base,
    padding: '12px 16px',
    boxShadow: shadows.dark.lg,
    color: colors.neutral[50],
    fontSize: '14px',
    fontWeight: 500,
  },

  // Progress bar styling
  progressBar: {
    height: 8,
    borderRadius: borderRadius.full,
    background: 'rgba(255, 255, 255, 0.08)',
    overflow: 'hidden',
    '& .MuiLinearProgress-bar': {
      background: gradients.primary,
      borderRadius: borderRadius.full,
      boxShadow: shadows.glow.subtle,
    },
  } as SxProps<Theme>,
};

// ===== BADGE & STATUS VARIANTS =====
export const badgeVariants = {
  success: {
    background: 'rgba(0, 255, 107, 0.15)',
    color: colors.primary[400],
    border: `1.5px solid ${colors.primary[500]}`,
    borderRadius: borderRadius.md,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: 600,
    boxShadow: shadows.glow.subtle,
  } as SxProps<Theme>,

  error: {
    background: 'rgba(255, 0, 64, 0.15)',
    color: colors.semantic.error,
    border: `1.5px solid ${colors.semantic.error}`,
    borderRadius: borderRadius.md,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: 600,
  } as SxProps<Theme>,

  warning: {
    background: 'rgba(250, 255, 0, 0.15)',
    color: colors.semantic.warning,
    border: `1.5px solid ${colors.semantic.warning}`,
    borderRadius: borderRadius.md,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: 600,
  } as SxProps<Theme>,

  info: {
    background: 'rgba(0, 224, 255, 0.15)',
    color: colors.secondary.blue[400],
    border: `1.5px solid ${colors.secondary.blue[500]}`,
    borderRadius: borderRadius.md,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: 600,
    boxShadow: shadows.glow.blue,
  } as SxProps<Theme>,

  neutral: {
    background: 'rgba(255, 255, 255, 0.08)',
    color: colors.neutral[300],
    border: '1.5px solid rgba(255, 255, 255, 0.12)',
    borderRadius: borderRadius.md,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: 600,
  } as SxProps<Theme>,
};

// ===== INPUT & FORM VARIANTS =====
export const inputVariants = {
  standard: {
    '& .MuiOutlinedInput-root': {
      background: 'rgba(255, 255, 255, 0.03)',
      backdropFilter: 'blur(10px)',
      borderRadius: borderRadius.md,
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        borderWidth: '1.5px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&.Mui-focused': {
        background: 'rgba(255, 255, 255, 0.05)',
        boxShadow: `0 0 0 3px rgba(0, 255, 107, 0.1)`,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primary[500],
        borderWidth: '2px',
      },
    },
  } as SxProps<Theme>,

  glass: {
    '& .MuiOutlinedInput-root': {
      background: gradients.glass.light,
      backdropFilter: 'blur(20px)',
      borderRadius: borderRadius.md,
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.15)',
        borderWidth: '1.5px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.25)',
      },
      '&.Mui-focused': {
        background: gradients.glass.medium,
        boxShadow: `0 0 0 3px rgba(0, 255, 107, 0.15)`,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primary[500],
        borderWidth: '2px',
      },
    },
  } as SxProps<Theme>,
};

// ===== ANIMATION UTILITIES =====
export const animations = {
  fadeIn: {
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    animation: 'fadeIn 0.4s ease-out',
  } as SxProps<Theme>,

  slideInRight: {
    '@keyframes slideInRight': {
      from: { opacity: 0, transform: 'translateX(20px)' },
      to: { opacity: 1, transform: 'translateX(0)' },
    },
    animation: 'slideInRight 0.4s ease-out',
  } as SxProps<Theme>,

  scaleIn: {
    '@keyframes scaleIn': {
      from: { opacity: 0, transform: 'scale(0.9)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animation: 'scaleIn 0.3s ease-out',
  } as SxProps<Theme>,

  pulse: {
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.7 },
    },
    animation: 'pulse 2s ease-in-out infinite',
  } as SxProps<Theme>,

  shimmer: {
    '@keyframes shimmer': {
      '0%': { backgroundPosition: '-1000px 0' },
      '100%': { backgroundPosition: '1000px 0' },
    },
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
    backgroundSize: '1000px 100%',
    animation: 'shimmer 2s infinite',
  } as SxProps<Theme>,
};

export default {
  cardVariants,
  buttonVariants,
  chartStyles,
  badgeVariants,
  inputVariants,
  animations,
};
